// packages/ui-kit/src/primitives/Dropdown.tsx
// C3F v18 — reference: design/system/components/navigation/Dropdown.{jsx,prompt.md}
import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "../lib/cn.js";

type Item =
  | { type: "item"; label: string; onClick: () => void; icon?: ReactNode; danger?: boolean }
  | { type: "separator" }
  | { type: "header"; label: string };

interface Props {
  trigger: ReactNode;
  items: Item[];
  align?: "left" | "right" | "center";
  /** Menu panel width in px (DS default 240). */
  width?: number;
  triggerAriaLabel?: string;
}

/** Pop-up menu. Closes on outside click and on selection. */
export function Dropdown({
  trigger,
  items,
  align = "right",
  width = 240,
  triggerAriaLabel,
}: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const alignCls =
    align === "left" ? "left-0" : align === "center" ? "left-1/2 -translate-x-1/2" : "right-0";

  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex cursor-pointer rounded-md focus-visible:outline-none focus-visible:shadow-focus"
        {...(triggerAriaLabel ? { "aria-label": triggerAriaLabel } : {})}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {trigger}
      </button>
      {open && (
        <div
          role="menu"
          style={{ width }}
          className={cn(
            "absolute z-40 top-full mt-2 max-w-[calc(100vw-1rem)] bg-elevated border border-border-default rounded-md shadow-lg p-1.5 [animation:px-pop_var(--dur-fast)_var(--ease-out)_both]",
            alignCls,
          )}
        >
          {items.map((it, i) => {
            if (it.type === "separator")
              return <div key={i} className="h-px bg-border-subtle mx-1 my-1.5" />;
            if (it.type === "header")
              return (
                <div key={i} className="text-eyebrow px-2.5 pt-2 pb-1">
                  {it.label}
                </div>
              );
            return (
              <button
                key={i}
                type="button"
                role="menuitem"
                onClick={() => {
                  it.onClick();
                  setOpen(false);
                }}
                className={cn(
                  // 40px rows from the DS, held to the 44px touch floor on
                  // phone viewports (staff taps with gloves) like Button sm.
                  "flex min-h-touch sm:min-h-10 w-full items-center gap-2.5 rounded-sm px-2.5 text-left text-sm font-medium cursor-pointer transition-colors duration-fast hover:bg-hover focus-visible:outline-none focus-visible:shadow-focus",
                  it.danger ? "text-danger" : "text-fg-default",
                )}
              >
                {it.icon}
                {it.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
